/**
 * Fixture-mode helpers for the Work Log Review hook.
 *
 * The 159a backend route does the heavy lifting (group-key construction,
 * source-aware hours math, capping, paginating). In fixture mode we
 * approximate the same behaviour client-side over the canned event list
 * so the page renders sensibly when the user toggles group-by, sort, or
 * filters.
 *
 * Hours math is a deliberate simplification: every event counts as
 * 5 minutes (matches the default minutes-per-screenshot for the
 * Insightful source — see config.classification.minutesPerScreenshot).
 * Real source-awareness lives in the backend; the fixture has no
 * `import_visualtime` events so the simplification is exact for the
 * data we have.
 */

import { dayOfWeekInTz, WEEKDAY_NAMES_LONG } from '@/lib/dates';
import type {
  GroupByDimension,
  SortByColumn,
  WorkLogReviewEventRow,
  WorkLogReviewGroupRow,
  WorkLogReviewQuery,
  WorkLogReviewResponse,
} from '@/types/workLogReview';

const FIXTURE_MINUTES_PER_EVENT = 5;
const SENTINEL = '(none)';

export interface FixtureContext {
  source: WorkLogReviewResponse;
  groupCap: number;
}

function passesClassificationFilter(
  e: WorkLogReviewEventRow,
  sources: WorkLogReviewQuery['classificationSource'],
): boolean {
  if (!sources || sources.length === 0) return true;
  return e.classificationSource !== null && sources.includes(e.classificationSource);
}

function passesEmployeeFilter(
  e: WorkLogReviewEventRow,
  employeeIds: readonly string[] | undefined,
): boolean {
  if (!employeeIds || employeeIds.length === 0) return true;
  return e.employeeId !== null && employeeIds.includes(e.employeeId);
}

function passesProjectFilter(e: WorkLogReviewEventRow, projectId: string | undefined): boolean {
  if (!projectId) return true;
  return e.project?.id === projectId;
}

function passesClientFilter(e: WorkLogReviewEventRow, clientId: string | undefined): boolean {
  if (!clientId) return true;
  return e.project?.clientId === clientId;
}

function passesRuleFilter(
  e: WorkLogReviewEventRow,
  classifiedByRuleId: string | undefined,
): boolean {
  if (!classifiedByRuleId) return true;
  return e.classifiedByRuleId === classifiedByRuleId;
}

/**
 * Apply the Work Log Review URL filters to the canned event list. Mirrors
 * the backend route's WHERE chain (sans `dateRange` and `businessUnitId`,
 * which the fixture's tiny corpus does not need to model).
 *
 * Exported for unit tests.
 */
export function filterEvents(
  events: readonly WorkLogReviewEventRow[],
  query: WorkLogReviewQuery,
): WorkLogReviewEventRow[] {
  return events.filter(
    (e) =>
      passesClassificationFilter(e, query.classificationSource) &&
      passesEmployeeFilter(e, query.employeeIds) &&
      passesProjectFilter(e, query.projectId) &&
      passesClientFilter(e, query.clientId) &&
      passesRuleFilter(e, query.classifiedByRuleId),
  );
}

function extractGroupValue(e: WorkLogReviewEventRow, dim: GroupByDimension): string | null {
  switch (dim) {
    case 'app':
      return e.app;
    case 'appFileName':
      return e.appFileName;
    case 'title':
      return e.title;
    case 'rootDomain':
      return e.rootDomain;
    case 'site':
      return e.site;
    case 'url':
      return e.url;
    case 'employee':
      return e.employeeName;
    case 'businessUnit':
      // Fixture event rows don't carry BU; the backend joins it. Return
      // null so the fixture-mode group renders "(none)" rather than
      // mis-guessing.
      return null;
    case 'project':
      return e.project?.name ?? null;
    case 'client':
      return e.project?.clientName ?? null;
    case 'classificationSource':
      return e.classificationSource;
    case 'classifiedByRule':
      return e.classifiedByRuleId;
    case 'day':
      return e.occurredAt.slice(0, 10);
    case 'hour':
      return e.occurredAt.slice(0, 13) + ':00';
    case 'weekday': {
      // dayOfWeekInTz wants a YYYY-MM-DD; the event ISO already starts
      // with one. Anchored at noon UTC inside the helper so DST cannot
      // shift the result.
      const idx = dayOfWeekInTz(e.occurredAt.slice(0, 10), 'UTC');
      return WEEKDAY_NAMES_LONG[idx];
    }
  }
}

interface GroupBucket {
  display: Record<string, string | null>;
  hash: string;
  events: WorkLogReviewEventRow[];
  employees: Set<string>;
  firstSeenAt: string;
  lastSeenAt: string;
}

/**
 * Build a stacked group key for a single event. Returns the display map
 * (echoed back in the response) and a hash string for bucketing.
 *
 * Exported for unit tests.
 */
export function buildGroupKey(
  e: WorkLogReviewEventRow,
  groupBy: readonly GroupByDimension[],
): { display: Record<string, string | null>; hash: string } {
  const display: Record<string, string | null> = {};
  const parts: string[] = [];
  for (const dim of groupBy) {
    const val = extractGroupValue(e, dim);
    display[dim] = val;
    parts.push(`${dim}=${val ?? SENTINEL}`);
  }
  return { display, hash: parts.join('|') };
}

function compareGroupRows(
  a: WorkLogReviewGroupRow,
  b: WorkLogReviewGroupRow,
  sortBy: SortByColumn,
): number {
  const av = a[sortBy];
  const bv = b[sortBy];
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  return String(av).localeCompare(String(bv));
}

/**
 * Sort group rows by the requested column + direction. Mutates the input
 * (matches the backend's `sortGroups` call shape).
 *
 * Exported for unit tests.
 */
export function sortGroupRows(
  rows: WorkLogReviewGroupRow[],
  sortBy: SortByColumn,
  sortDir: 'asc' | 'desc',
): WorkLogReviewGroupRow[] {
  rows.sort((a, b) => {
    const cmp = compareGroupRows(a, b, sortBy);
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return rows;
}

/**
 * Build the per-group summary rows from a filtered event list. Empty
 * `groupBy` collapses every event into a single bucket with an empty
 * key map (matches the backend's behaviour).
 */
export function buildGroupRows(
  events: readonly WorkLogReviewEventRow[],
  groupBy: readonly GroupByDimension[],
): WorkLogReviewGroupRow[] {
  const buckets = new Map<string, GroupBucket>();
  for (const e of events) {
    const { display, hash } = buildGroupKey(e, groupBy);
    let bucket = buckets.get(hash);
    if (!bucket) {
      bucket = {
        display,
        hash,
        events: [],
        employees: new Set(),
        firstSeenAt: e.occurredAt,
        lastSeenAt: e.occurredAt,
      };
      buckets.set(hash, bucket);
    }
    bucket.events.push(e);
    if (e.employeeId) bucket.employees.add(e.employeeId);
    if (e.occurredAt < bucket.firstSeenAt) bucket.firstSeenAt = e.occurredAt;
    if (e.occurredAt > bucket.lastSeenAt) bucket.lastSeenAt = e.occurredAt;
  }
  const totalCount = events.length;
  const rows: WorkLogReviewGroupRow[] = [];
  for (const b of buckets.values()) {
    const count = b.events.length;
    const hours = +((count * FIXTURE_MINUTES_PER_EVENT) / 60).toFixed(2);
    rows.push({
      key: b.display,
      count,
      hours,
      distinctEmployees: b.employees.size,
      firstSeenAt: b.firstSeenAt,
      lastSeenAt: b.lastSeenAt,
      percentOfFiltered: totalCount === 0 ? 0 : +((count / totalCount) * 100).toFixed(2),
    });
  }
  return rows;
}

/**
 * Top-level fixture computation: filter, group, sort, paginate. Returns a
 * `WorkLogReviewResponse` shaped exactly like the backend would for the
 * given URL state.
 *
 * Exported for unit tests + the data hook.
 */
export function computeFixtureResponse(
  ctx: FixtureContext,
  query: WorkLogReviewQuery,
): WorkLogReviewResponse {
  const groupBy = query.groupBy ?? [];
  const classificationSource =
    query.classificationSource && query.classificationSource.length > 0
      ? [...query.classificationSource]
      : ['fallback' as const];
  const sortBy: SortByColumn = query.sortBy ?? 'hours';
  const sortDir: 'asc' | 'desc' = query.sortDir ?? 'desc';
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? 100;

  const filtered = filterEvents(ctx.source.events.rows, {
    ...query,
    classificationSource,
  });

  // Sort the raw events by occurredAt DESC, mirroring the backend's
  // raw-event ordering (the page log is sorted by time, not by group).
  const sortedEvents = [...filtered].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));

  const groupRows = buildGroupRows(filtered, groupBy);
  sortGroupRows(groupRows, sortBy, sortDir);

  const distinctGroups = groupRows.length;
  const cappedGroups = distinctGroups > ctx.groupCap ? groupRows.slice(0, ctx.groupCap) : groupRows;
  // Honour the source fixture's `groupCapHit` when it's already set
  // (cap-hit fixture variant), otherwise compute from the live count.
  const groupCapHit = ctx.source.groupCapHit || distinctGroups > ctx.groupCap;

  const distinctEmployeeIds = new Set<string>();
  for (const e of filtered) {
    if (e.employeeId) distinctEmployeeIds.add(e.employeeId);
  }
  const totalHours = +((filtered.length * FIXTURE_MINUTES_PER_EVENT) / 60).toFixed(2);

  const offset = (page - 1) * pageSize;
  const pageRows = sortedEvents.slice(offset, offset + pageSize);

  return {
    filter: {
      from: ctx.source.filter.from,
      to: ctx.source.filter.to,
      classificationSource,
      employeeIds: query.employeeIds,
      businessUnitId: query.businessUnitId,
      classifiedByRuleId: query.classifiedByRuleId,
      projectId: query.projectId,
      clientId: query.clientId,
      groupBy: [...groupBy],
      sortBy,
      sortDir,
    },
    totals: {
      eventCount: filtered.length,
      hours: totalHours,
      distinctEmployees: distinctEmployeeIds.size,
      // Honour the source fixture's distinctGroups when it's pinned higher
      // than our computed count (cap-hit variant): the fixture advertises
      // 612 groups but only carries 14 events, so the "612 groups" hint
      // stays realistic.
      distinctGroups: Math.max(distinctGroups, ctx.source.totals.distinctGroups),
    },
    groups: cappedGroups,
    groupCapHit,
    groupCap: ctx.groupCap,
    events: {
      page,
      pageSize,
      totalCount: filtered.length,
      rows: pageRows,
    },
  };
}
